import "./transit-map.css";

import { useMutationState } from "@tanstack/react-query";
import type { ButtonProps as KendoButtonProps } from "@tql/ui-components-react/kendo/buttons";
import {
	Button as KendoButton,
	ButtonGroup,
} from "@tql/ui-components-react/kendo/buttons";
import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { undoIcon } from "@tql/ui-components-react/kendo/svg-icons";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	checkCircle,
	clock,
	packageCheck,
	refreshCcw_02,
	xClose,
} from "@tql/ui-components-react/untitled/icons";
import { memo, useState } from "react";
import { useParams } from "react-router";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { environmentConfig } from "~/shared/utils/environment-config.ts";

import { usePostCancelDispatch } from "../../hooks/use-post-cancel-dispatch.ts";
import type { AugmentedShipment } from "../../types/augmented-shipment.ts";
import { BillHoldModal } from "../bill-hold-modal/index.tsx";
import { MapContent } from "./components/map/index.tsx";
import { MarkDeliveredModal } from "./components/mark-delivered-modal/index.tsx";
import { MarkUnloadedModal } from "./components/unmark-loaded-modal/index.tsx";
import { useGetEdiStatus } from "./hooks/use-get-edi-status.ts";

const EDI_STATUS_DISPLAY: Record<
	number,
	{ label: string; themeColor: KendoButtonProps["themeColor"] }
> = {
	1: { label: "EDI Compliant", themeColor: "success" },
	2: { label: "EDI Event Due Soon", themeColor: "warning" },
	3: { label: "EDI Not Compliant", themeColor: "error" },
};

const openEdiManager = (poNumber: number) => {
	const ediHost =
		{ local: "edidev", dev: "edidev", stage: "ediqa" }[
			environmentConfig.environmentName ?? ""
		] ?? "edi";
	window.open(
		// EDI Manager does not listen on HTTPS.
		`http://${ediHost}.tql.com/edimanager/lmindex#/events/${poNumber}`,
		"_blank",
		"noopener,noreferrer",
	);
};

export const TransitMap = memo(
	({ shipment }: { shipment: AugmentedShipment }) => {
		const [isUnmarkLoadedOpen, setIsUnmarkLoadedOpen] = useState(false);
		const [isMarkDeliveredOpen, setIsMarkDeliveredOpen] = useState(false);
		const params = useParams();
		const poNumber = Number(params["poNumber"]);
		const validPoNumber =
			Number.isFinite(poNumber) && poNumber > 0 ? poNumber : null;

		// Observe the mark-delivered mutation across hook instances (it is fired from
		// inside the modal) so the spinner covers the save + shipment refetch.
		const isSavingMarkDelivered =
			useMutationState({
				filters: { mutationKey: ["markDelivered"] },
				select: (mutation) => mutation.state.status,
			}).at(-1) === "pending";
		const [isBillHoldOpen, setIsBillHoldOpen] = useState(false);
		const { cancelDispatch, isSavingCancelDispatch } = usePostCancelDispatch();
		const {
			getEdiStatus,
			isLoadingEdiStatus,
			loadComplianceDetail: refreshedComplianceDetail,
		} = useGetEdiStatus();
		const ediStatus =
			refreshedComplianceDetail?.loadComplianceStatusID ??
			shipment?.loadComplianceDetail?.loadComplianceStatusID ??
			null;
		const ediDisplay = ediStatus ? EDI_STATUS_DISPLAY[ediStatus] : undefined;

		return (
			<>
				<LoadingSpinner
					isVisible={
						isSavingMarkDelivered ||
						isSavingCancelDispatch ||
						isLoadingEdiStatus
					}
				/>
				<MarkUnloadedModal
					isOpen={isUnmarkLoadedOpen}
					onClose={() => setIsUnmarkLoadedOpen(false)}
					poNumber={String(validPoNumber)}
				/>
				<MarkDeliveredModal
					isOpen={isMarkDeliveredOpen}
					onClose={() => setIsMarkDeliveredOpen(false)}
				/>
				<MapContent />
				<ButtonGroup className={"map-actions"}>
					<div className={"button-row"}>
						<Button onClick={() => setIsUnmarkLoadedOpen(true)}>
							<SvgIcon className={"shipment-progress-icon"} icon={undoIcon} />
							Unmark Loaded
						</Button>
						<Button
							disabled={isSavingMarkDelivered}
							onClick={() => setIsMarkDeliveredOpen(true)}
							variant={"primary"}
						>
							<SvgIcon
								className={"shipment-progress-icon"}
								icon={packageCheck}
							/>
							Mark Delivered
						</Button>
					</div>
					<Button
						disabled={isSavingCancelDispatch}
						onClick={() =>
							cancelDispatch({
								poNumber: Number(params["poNumber"]),
								customerId: shipment.customer.id,
							})
						}
						variant={"dangerSecondary"}
					>
						<SvgIcon className={"shipment-progress-icon"} icon={xClose} />
						Cancel Dispatch
					</Button>

					{shipment?.loadComplianceDetail?.isEDICustomer ? (
						<div className={"button-row"}>
							{/* eslint-disable-next-line @typescript-eslint/no-deprecated -- TQL Button has no success/warning variants */}
							<KendoButton
								disabled={!ediDisplay || validPoNumber === null}
								fillMode={"outline"}
								onClick={() => {
									if (validPoNumber !== null) openEdiManager(validPoNumber);
								}}
								rounded={"medium"}
								themeColor={ediDisplay?.themeColor ?? "primary"}
								type={"button"}
							>
								<SvgIcon
									className={"shipment-progress-icon"}
									icon={checkCircle}
								/>
								{ediDisplay?.label ?? "EDI Status Unknown"}
							</KendoButton>
							<Button
								disabled={isLoadingEdiStatus}
								onClick={() => {
									getEdiStatus({
										poNumber: String(params["poNumber"]),
										customerId: String(shipment?.customer?.id ?? ""),
									});
								}}
							>
								<SvgIcon
									className={"shipment-progress-icon"}
									icon={refreshCcw_02}
								/>
								Refresh EDI
							</Button>
						</div>
					) : null}

					{shipment?.customer?.isBillHoldCustomer ? (
						// eslint-disable-next-line @typescript-eslint/no-deprecated -- TQL Button has no warning variant
						<KendoButton
							fillMode={"outline"}
							onClick={() => {
								setIsBillHoldOpen(true);
							}}
							rounded={"medium"}
							themeColor={"warning"}
							type={"button"}
						>
							<SvgIcon className={"shipment-progress-icon"} icon={clock} />
							Bill Hold
						</KendoButton>
					) : null}
				</ButtonGroup>
				<BillHoldModal
					isOpen={isBillHoldOpen}
					onClose={() => setIsBillHoldOpen(false)}
					shipment={shipment}
				/>
			</>
		);
	},
);

TransitMap.displayName = "TransitMap";
